import { CreatorChallenge } from '../../../types/challenges';
import ButtonComponent from '../../common/button-component/button-component';
import {
  formatChallengeEndDate,
  getChallengeProgressLabel,
  getChallengeTitle,
  isChallengeCompleted,
  isChallengeRewardClaimable,
} from '../../../utils/challenge-utils';
import ClockIcon from '../../../assets/icons/challenge-clock.svg';
import ChevronIcon from '../../../assets/icons/challenge-chevron.svg';
import styles from './challengesSection.module.css';

type ChallengeRowProps = {
  challenge: CreatorChallenge;
  defaultCurrency: string;
  onClaimReward: (challenge: CreatorChallenge) => void;
  onOpenDetails?: (challenge: CreatorChallenge) => void;
};

export default function ChallengeRow({ challenge, defaultCurrency, onClaimReward, onOpenDetails }: ChallengeRowProps) {
  const isClaimable = isChallengeRewardClaimable(challenge);
  const showEndDate = !isClaimable && Boolean(challenge.endDate);

  const openDetails = () => onOpenDetails?.(challenge);

  const row = (
    <>
      <div className={styles.challengeTop}>
        <p className={styles.challengeTitle}>{getChallengeTitle(challenge, defaultCurrency)}</p>
        <img src={ChevronIcon} alt="" className={styles.challengeChevron} width={24} height={24} />
      </div>

      <div className={styles.progressTrack}>
        <div
          className={`${styles.progressFill} ${isChallengeCompleted(challenge) ? styles.progressFillComplete : ''}`}
          style={{ width: `${Math.min(100, Math.max(0, challenge.progress.percentage))}%` }}
        />
      </div>

      <div className={`${styles.progressDetails} ${isClaimable ? styles.progressDetailsClaimable : ''}`}>
        {isClaimable && (
          <ButtonComponent
            variant="primaryOutline"
            className={styles.claimRewardButton}
            textStyle={{ fontSize: '14px', fontWeight: 500 }}
            onClick={(event) => {
              event.stopPropagation();
              onClaimReward(challenge);
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            Claim reward
          </ButtonComponent>
        )}
        {showEndDate && (
          <div className={styles.endDateTag}>
            <img src={ClockIcon} alt="" width={16} height={16} />
            <span>{formatChallengeEndDate(challenge.endDate as string)}</span>
          </div>
        )}
        <p className={styles.progressLabel}>{getChallengeProgressLabel(challenge)}</p>
      </div>
    </>
  );

  if (onOpenDetails) {
    return (
      <div
        className={`${styles.challengeRow} ${styles.challengeRowClickable}`}
        role="button"
        tabIndex={0}
        onClick={openDetails}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDetails();
          }
        }}
      >
        {row}
      </div>
    );
  }

  return <div className={styles.challengeRow}>{row}</div>;
}
